/**
 * Whether a conversation belongs in the main chat surface (popover):
 * marketplace listing conversations are buyer-initiated, listing-scoped
 * threads, so buyers should only see them on the listing page and the
 * Marketplace > Messages inbox — not mixed into the main chat. Sellers keep
 * them in the main chat so incoming buyer questions surface organically.
 *
 * A conversation is hidden when:
 *   type === 'MARKETPLACE_LISTING' AND sellerTeamId is non-empty AND
 *   sellerTeamId !== the viewer's team id (i.e. the viewer is the buyer).
 *
 * Legacy marketplace conversations created before `sellerTeamId` was stored
 * have an empty sellerTeamId. We can't tell buyer from seller for those, and
 * hiding them would make a seller miss messages — so we keep showing them in
 * the main chat (legacy behavior).
 */
export const isConversationVisibleInMainChat = (conversation, teamId) => {
  if ((conversation?.type || '').toUpperCase() !== 'MARKETPLACE_LISTING') {
    return true;
  }
  const sellerTeamId = conversation?.sellerTeamId;
  if (!sellerTeamId) {
    return true; // legacy doc without sellerTeamId — see comment above
  }
  return String(sellerTeamId) === String(teamId);
};

export const getConversationType = (conversation, teamId) => {
  if (conversation.type) {
    const type = conversation.type.toUpperCase();
    if (type === 'TEAM') {
      return conversation.teamId === teamId ? 'TEAM' : undefined;
    }
    if (type === 'ADMIN') {
      return conversation.teamId === teamId ? 'ADMIN' : undefined;
    }
    return type;
  }
};

export const getUnreadMessagesCount = (conversations, user) => {
  return Object.values(conversations)
    .map((conversation) => {
      const type = getConversationType(conversation, user?.team_id);
      if (type === 'TEAM' && !user?.team_chat_enabled) {
        return 0;
      } else if (type === 'ADMIN' && !user?.admin_chat_enabled) {
        return 0;
      }

      // The popover badge must match the popover list: don't count buyer-side
      // marketplace conversations that are hidden from the main chat.
      if (!isConversationVisibleInMainChat(conversation, user?.team_id)) {
        return 0;
      }

      return conversation.unreadCount || 0;
    })
    .reduce((acc, x) => acc + x, 0);
};
